import React from "react";
import { createPortal } from "react-dom";
import { Typography } from "@mui/material";
import { DashboardTile } from "../../store/types.tsx";
import { tallyTokens } from "../../theme.tsx";
import DashboardTileCard from "./DashboardTileCard.tsx";
import DashboardTilePlaceholderCard from "./DashboardTilePlaceholderCard.tsx";
import "../../styles/dashboard-print.css";

/** The grid is laid out at the dashboard's widest on-screen width, so charts
 *  render at the size people know, and is then scaled to fit the page. */
export const PRINT_GRID_WIDTH = 1180;
/** Printable width of A4 landscape with 12mm margins, in CSS pixels. */
const PRINTABLE_WIDTH = ((297 - 2 * 12) / 25.4) * 96;
const PRINT_ZOOM = Math.min(1, PRINTABLE_WIDTH / PRINT_GRID_WIDTH);

export type PrintLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

interface DashboardPrintViewProps {
  title: string;
  subtitle: string;
  layout: PrintLayoutItem[];
  tilesById: Record<string, DashboardTile>;
  tileTypesById: Record<string, string>;
}

/**
 * A print-only copy of the dashboard, rendered straight under <body> so it
 * is not clipped by the app's scroll containers. On screen it sits off to the
 * side, invisible, while the charts measure themselves; under `@media print`
 * it is the only thing on the page. Tiles keep their saved columns and rows,
 * but in a CSS grid rather than absolute positions, so a tile that would
 * straddle a page break moves to the next page instead of being cut.
 */
const DashboardPrintView: React.FC<DashboardPrintViewProps> = ({
  title,
  subtitle,
  layout,
  tilesById,
  tileTypesById,
}) =>
  createPortal(
    <div
      className="dashboard-print"
      aria-hidden="true"
      style={
        {
          width: PRINT_GRID_WIDTH,
          "--dashboard-print-zoom": PRINT_ZOOM,
        } as React.CSSProperties
      }
    >
      <Typography
        component="h1"
        sx={{
          margin: 0,
          fontSize: "25px",
          lineHeight: 1.55,
          letterSpacing: "-0.02em",
          color: tallyTokens.ink,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          margin: "0 0 22px",
          fontSize: "14px",
          lineHeight: 1.55,
          color: tallyTokens.ink2,
        }}
      >
        {subtitle}
      </Typography>
      <div className="dashboard-print__grid">
        {layout.map((item) => {
          const tile = tilesById[item.i];
          return (
            <div
              key={item.i}
              className="dashboard-print__tile"
              style={{
                gridColumn: `${item.x + 1} / span ${item.w}`,
                gridRow: `${item.y + 1} / span ${item.h}`,
              }}
            >
              {tile ? (
                <DashboardTileCard tile={tile} fillHeight readOnly />
              ) : (
                <DashboardTilePlaceholderCard
                  type={tileTypesById[item.i]}
                  fillHeight
                />
              )}
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  );

export default DashboardPrintView;
